// src/utils/taxProvision.js
//
// Applying / removing a preparer-CONFIRMED indicative income tax provision
// (29 Sep 2026, kailux-fixes-log.md §88).
//
// Income tax only ever enters the statements from a mapped "Income Tax Expense"
// account, so a run with a profit and no tax silently presents pre-tax profit as
// the profit for the year. When the preparer explicitly chooses to add an
// indicative provision, it is recorded as the two-sided journal an accountant
// would post:
//
//     Dr  Income tax expense — indicative provision      R x
//         Cr  Provision for income tax — indicative       R x
//
// as two real accounts in the account list (mapped to "Income Tax Expense" and
// "Tax Payables"). Everything downstream — server-side aggregation, the trial-
// balance and balance-sheet checks, the tax reasonableness check, notes,
// deferred tax, the PDF — then treats it like any other tax account with no
// special-casing, and the trial balance still balances. For flows with no
// account list (bank statement) the same amounts are added to the figures.
//
// Pure functions only (inputs are never mutated). Nothing here DECIDES whether a
// provision is needed or how much — that is tax_provision_gap() on the server
// plus the preparer's own confirmation.

export const TAX_PROVISION_CODES = { expense: 'ADJ-TAX-EXP', payable: 'ADJ-TAX-PAY' }

const EXPENSE_NAME = 'Income tax expense — indicative provision (preparer-confirmed)'
const PAYABLE_NAME = 'Provision for income tax — indicative (preparer-confirmed)'

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100

export const isTaxProvisionAccount = (acc) =>
  !!acc && (acc.code === TAX_PROVISION_CODES.expense || acc.code === TAX_PROVISION_CODES.payable)

// The provision amount already carried in an account list (0 if none).
export const taxProvisionAmountIn = (accounts) => {
  const row = (accounts || []).find(a => a.code === TAX_PROVISION_CODES.expense)
  return row ? round2(parseFloat(row.debit || 0)) : 0
}

// The account list without any provision rows, plus the amount that was in it.
export const extractTaxProvision = (accounts) => ({
  amount: taxProvisionAmountIn(accounts),
  accounts: (accounts || []).filter(a => !isTaxProvisionAccount(a)),
})

const withoutProvisionMappings = (mappings) => {
  const m = { ...(mappings || {}) }
  delete m[TAX_PROVISION_CODES.expense]
  delete m[TAX_PROVISION_CODES.payable]
  return m
}

const bump = (figures, key, delta) => {
  const next = round2((parseFloat(figures[key]) || 0) + delta)
  if (Math.abs(next) < 0.005) delete figures[key]; else figures[key] = next
}

// Remove a previously-applied provision from a figures object.
export const withoutTaxProvisionFigures = (figures, amount) => {
  if (!figures) return figures
  const f = { ...figures }
  if (amount) {
    bump(f, 'Income Tax Expense', -amount)
    bump(f, 'Tax Payables', -amount)
  }
  return f
}

// Apply a provision. Idempotent for the account list (any earlier provision rows
// are replaced, never duplicated). `figures` must be the figures WITHOUT the
// provision — callers establish that first (see Review.jsx).
export const applyTaxProvision = ({ accounts, mappings, figures }, amount) => {
  const amt = round2(amount)
  if (!(amt > 0)) throw new Error('A provision amount greater than zero is required')
  const baseAccounts = (accounts || []).filter(a => !isTaxProvisionAccount(a))
  const hasAccountList = baseAccounts.length > 0
  const out = {
    accounts: hasAccountList
      ? [
          ...baseAccounts,
          { code: TAX_PROVISION_CODES.expense, name: EXPENSE_NAME, debit: amt, credit: 0, source: 'tax_provision' },
          { code: TAX_PROVISION_CODES.payable, name: PAYABLE_NAME, debit: 0, credit: amt, source: 'tax_provision' },
        ]
      : (accounts || []),
    mappings: hasAccountList
      ? {
          ...withoutProvisionMappings(mappings),
          [TAX_PROVISION_CODES.expense]: { ifrs_category: 'Income Tax Expense', confidence: 100, source: 'manual', confirmed: true, tax_provision: true },
          [TAX_PROVISION_CODES.payable]: { ifrs_category: 'Tax Payables',       confidence: 100, source: 'manual', confirmed: true, tax_provision: true },
        }
      : (mappings || {}),
    figures: figures ? { ...figures } : figures,
  }
  if (out.figures) {
    bump(out.figures, 'Income Tax Expense', amt)
    bump(out.figures, 'Tax Payables', amt)
  }
  return out
}

// Strip a provision out of {accounts, mappings, figures} entirely.
export const removeTaxProvision = ({ accounts, mappings, figures }) => {
  const { amount, accounts: stripped } = extractTaxProvision(accounts)
  return {
    accounts: stripped,
    mappings: withoutProvisionMappings(mappings),
    figures: withoutTaxProvisionFigures(figures, amount),
    amount,
  }
}

// ── §108: the two tax bases ──────────────────────────────────────────────────
export const BASIS_LABELS = { standard: 'standard company rate', sbc: 'small business corporation' }
export const basisLabel = (basis) => BASIS_LABELS[basis] || ''

// The most a provision may be on a given basis: never more than profit before tax, and — for a small
// business corporation, whose sliding scale can never charge more than the flat rate — never more than
// the standard-rate amount. Mirrors the server, which re-checks both.
export const provisionCap = (preview, basis) => {
  if (!preview) return 0
  const pbt = Number(preview.pbt) || 0
  if (basis === 'sbc') {
    const flat = Number(preview.indicative_amount)
    return Number.isFinite(flat) && flat > 0 ? Math.min(pbt, flat) : pbt
  }
  return pbt
}

// '' when the amount is acceptable, otherwise the plain-language reason (same wording the server uses).
export const provisionAmountError = (amount, preview, basis, fmt) => {
  if (Number.isNaN(amount) || amount <= 0) return 'Enter an amount greater than zero.'
  if (basis === 'sbc' && amount > Number(preview.indicative_amount) + 0.005) {
    return `A small business corporation provision cannot exceed the standard-rate amount (${fmt(preview.indicative_amount)}).`
  }
  if (amount > Number(preview.pbt) + 0.005) return `The provision cannot exceed profit before tax (${fmt(preview.pbt)}).`
  return ''
}

